import { useEffect, useRef } from "react";
import { toast } from "sonner";

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const VERSION_URL = "/version.json";
const TOAST_ID = "cc-os-version-update";

type VersionPayload = {
  version: string;
  builtAt?: string;
};

async function fetchDeployedVersion(): Promise<string | null> {
  try {
    const res = await fetch(VERSION_URL, { cache: "no-store" });
    if (!res.ok) return null;
    const data = (await res.json()) as VersionPayload;
    return typeof data?.version === "string" ? data.version : null;
  } catch {
    return null;
  }
}

function notifyOnce(currentVersion: string, deployedVersion: string) {
  toast.message("New version available", {
    id: TOAST_ID,
    description: `Reload to load the latest update (${deployedVersion}).`,
    duration: Infinity,
    action: {
      label: "Reload",
      onClick: () => window.location.reload(),
    },
  });
  if (typeof console !== "undefined") {
    console.info(
      `[version-watcher] new deploy detected: ${currentVersion} → ${deployedVersion}`,
    );
  }
}

/**
 * useVersionWatcher — polls /version.json and toasts the user when a new
 * deploy is detected. Short-circuits in dev (where __APP_VERSION__ === "dev").
 *
 * Behaviour:
 *   - First mount: starts polling on a 5-min interval.
 *   - document.visibilityState === "hidden": skips polls to save battery.
 *   - Network/4xx errors: silent (no toast, no Sentry).
 *   - Mismatch: shows a sticky sonner toast with a "Reload" action.
 *   - Same mismatch detected on subsequent polls: no re-toast (dedupe via
 *     the lastDetectedRef + sonner toast id).
 *   - pageshow with persisted=true (bfcache restore): immediate check.
 */
export function useVersionWatcher() {
  const lastDetectedRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof __APP_VERSION__ === "undefined" || __APP_VERSION__ === "dev") {
      return;
    }
    const currentVersion = __APP_VERSION__;

    let cancelled = false;

    async function check() {
      if (cancelled) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      const deployed = await fetchDeployedVersion();
      if (cancelled || !deployed) return;
      if (deployed !== currentVersion && deployed !== lastDetectedRef.current) {
        lastDetectedRef.current = deployed;
        notifyOnce(currentVersion, deployed);
      }
    }

    const interval = window.setInterval(check, POLL_INTERVAL_MS);
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) void check();
    };
    window.addEventListener("pageshow", onPageShow);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, []);
}
